import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AuthStateService } from '../state/auth-state.service';
import { APP_CONFIGS } from '@dental-ui/shared/app-navigation';
import { Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import {
  AUTH_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  AUTH_PARAM_KEYS,
  AUTH_SESSION_END_EXPIRED,
  CAD_APP_NAME,
  isAccountApp,
} from '@dental-ui/shared/consts';

/**
 * Auth token'larını kontrol eder
 * Öncelik sırası: Cookie -> localStorage -> sessionStorage
 * accessToken ve refreshToken'ın her ikisinin de bulunması gerekir
 */
function checkAuthCookies(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }

  // 1. Önce cookie'lerden kontrol et
  const cookies = document.cookie.split(';');
  const hasAccessTokenCookie = cookies.some(c => c.trim().startsWith('accessToken='));
  const hasRefreshTokenCookie = cookies.some(c => c.trim().startsWith('refreshToken='));

  if (hasAccessTokenCookie && hasRefreshTokenCookie) {
    return true;
  }

  // 2. Cookie'lerde eksik varsa localStorage ve sessionStorage ile birleşik kontrol et
  if (typeof window !== 'undefined') {
    const hasAccessTokenStorage = !!(localStorage.getItem(AUTH_TOKEN_KEY) || sessionStorage.getItem(AUTH_TOKEN_KEY));
    const hasRefreshTokenStorage = !!(localStorage.getItem(REFRESH_TOKEN_KEY) || sessionStorage.getItem(REFRESH_TOKEN_KEY));

    const hasAccessToken = hasAccessTokenCookie || hasAccessTokenStorage;
    const hasRefreshToken = hasRefreshTokenCookie || hasRefreshTokenStorage;

    return hasAccessToken && hasRefreshToken;
  }

  return false;
}


/**
 * Mevcut uygulamanın adını döndürür (port veya subdomain'e göre)
 */
function getCurrentAppName(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const hostname = window.location.hostname;
  const port = window.location.port;

  // Localhost'ta port numarasına göre kontrol et
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    for (const [appName, config] of Object.entries(APP_CONFIGS)) {
      if (config?.devPort && port === config.devPort.toString()) {
        return appName;
      }
    }
  }

  // Production/Dev'da subdomain'e göre kontrol et
  const parts = hostname.split('.');
  if (parts.length > 2) {
    let subdomain = parts[0];
    // dev- prefix'ini temizle
    if (subdomain.startsWith('dev-')) {
      subdomain = subdomain.replace('dev-', '');
    }
    
    for (const [appName, config] of Object.entries(APP_CONFIGS)) {
      if (config?.subdomain === subdomain) {
        return appName;
      }
    }
  }

  return null;
}

/**
 * Genel authentication guard
 * Uygulama tipine göre farklı yönlendirmeler yapar
 */

export const authGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state?: RouterStateSnapshot) => {
  const authState = inject(AuthStateService);
  const router = inject(Router);

  // Önce URL parametrelerini kontrol et ve varsa cookie'lere yaz
  // Hash routing'de queryParams boş olabilir, window.location den de kontrol et
  const queryParams = { ...route.queryParams };
  let hasUrlParams = !!(queryParams['accessToken'] && queryParams['refreshToken']);

  // Hash routing desteği: window.location.search VEYA window.location.hash içindeki parametreleri oku
  if (!hasUrlParams && typeof window !== 'undefined') {
    const searchParams = new URLSearchParams(window.location.search);
    const hashParts = window.location.hash.split('?');
    const hashParams = hashParts.length > 1 ? new URLSearchParams(hashParts[1]) : new URLSearchParams();

    // accessToken ve refreshToken'ı hem search hem de hash'ten kontrol et
    const accessToken = searchParams.get('accessToken') || hashParams.get('accessToken');
    const refreshToken = searchParams.get('refreshToken') || hashParams.get('refreshToken');

    if (accessToken && refreshToken) {

      hasUrlParams = true;
      // Parametreleri topla
      const authKeys = ['accessToken', 'refreshToken', 'refreshTokenExpired', 'expiration', 'email', 'language', 'accountAppUrl', 'redirectTestUrl'];
      authKeys.forEach(key => {
        const val = searchParams.get(key) || hashParams.get(key);
        if (val) queryParams[key] = val;
      });
    }
  }

  // NOT: window.history.replaceState ile burada URL temizleme yapmıyoruz.
  // Angular navigation tamamlandıktan sonra kendi URL senkronizasyonunu çalıştırır
  // ve temizlediğimiz parametreleri geri ekler. Temizleme işlemi aşağıda
  // Angular Router'ın navigate() metodu ile yapılıyor.


  // URL parametrelerinden cookie'lere yazma işlemini başlat
  const urlParamsResult$ = authState.persistAuthParamsFromUrl(queryParams);

  return urlParamsResult$.pipe(
    switchMap((refreshSuccess) => {
      // URL parametreleri varsa ve cookie'lere yazıldıysa, cookie kontrolü yap
      if (hasUrlParams) {
        const hasAuthCookies = checkAuthCookies();

        if (hasAuthCookies) {
          if (refreshSuccess) {
            // Auth başarılı. Angular Router ile aynı rotaya navigate et.
            // Sadece auth parametrelerini temizle, uygulama parametrelerini (project vb.) koru.
            // window.history.replaceState Angular tarafından ezildiği için bu yöntem kullanılır.
            setTimeout(() => {
              const hashPath = window.location.hash.replace(/^#/, '').split('?')[0] || '/';
              // Auth parametrelerini filtrele, geri kalanları (project vb.) koru
              const currentParams = { ...route.queryParams };
              AUTH_PARAM_KEYS.forEach(key => delete currentParams[key]);
              router.navigate([hashPath], { queryParams: currentParams, replaceUrl: true });
            }, 0);
            return of(true);
          } else {
            return authState.tryRestoreSessionFromCookies().pipe(
              switchMap((success) => {
                if (success) {
                  setTimeout(() => {
                    const hashPath = window.location.hash.replace(/^#/, '').split('?')[0] || '/';
                    const currentParams = { ...route.queryParams };
                    AUTH_PARAM_KEYS.forEach(key => delete currentParams[key]);
                    router.navigate([hashPath], { queryParams: currentParams, replaceUrl: true });
                  }, 0);
                  return of(true);
                }
                navigateToLogin('koruma: URL oturum parametreleri var ama geri yükleme başarısız');
                return of(false);
              }),
              catchError((err) => {
                navigateToLogin(`koruma: URL oturum geri yükleme hatası ${err}`);
                return of(false);
              })
            );
          }
        } else {
          navigateToLogin('koruma: URL oturum parametreleri eksik (çerezler yok)');
          return of(false);
        }
      }

      // URL parametreleri yoksa, normal kontrol akışına devam et
      return checkAuthAndRestore();
    })
  );

  function checkAuthAndRestore(): Observable<boolean> {
    // Hem in-memory state'i hem de cookie/storage oturumunu kontrol et
    const isAuthenticated = authState.isAuthenticated();
    const hasAuth = checkAuthCookies();
    const currentApp = getCurrentAppName();

    if (isAuthenticated) {
      if (!authState.isTokenExpired()) {
        // CAD: Lab ile aynı porta localStorage paylaşılmaz — /Me ile bu origin'e yaz.
        // Tüm uygulamalar: communicationId yoksa /Me (SignalR ForceLogout grubu için).
        const missingCommunicationId = !authState.hasCommunicationId();
        const cadNeedsHydrate =
          currentApp === CAD_APP_NAME &&
          (!authState.hasSessionBeenWrittenViaMe() || !authState.canSilentRefresh());

        if (missingCommunicationId || cadNeedsHydrate) {
          console.info('[Auth][Guard] Auth/Me ile oturum hydrate ediliyor', {
            app: currentApp,
            missingCommunicationId,
            cadNeedsHydrate,
          });
          return authState.hydrateSessionViaMe().pipe(
            switchMap((ok) => {
              if (ok || (authState.isAuthenticated() && authState.canSilentRefresh())) {
                return of(true);
              }
              if (authState.isAuthenticated() && !authState.isRefreshTokenExpired()) {
                console.warn(
                  '[Auth][Guard] hydrate geçici başarısız — oturum korundu, sayfa açılıyor'
                );
                return of(true);
              }
              navigateToLogin('koruma: Auth/Me hydrate başarısız');
              return of(false);
            }),
            catchError((err) => {
              if (authState.isAuthenticated() && !authState.isRefreshTokenExpired()) {
                console.warn('[Auth][Guard] hydrate hatası (geçici) — oturum korundu', err);
                return of(true);
              }
              navigateToLogin(`koruma: Auth/Me hydrate hatası ${err}`);
              return of(false);
            })
          );
        }
        return of(true);
      } else {
        // Token süresi dolmuşsa, refresh token ile yenilemeyi dene
        console.info('[Auth][Guard] Erişim tokenı süresi dolmuş — token yenileniyor');
        return authState.ensureValidToken().pipe(
          switchMap((token) => {
            if (token) {
              console.info('[Auth][Guard] Yenileme başarılı — sayfa açılıyor');
              return of(true);
            }
            // Ağ/5xx soft-fail: oturum silinmediyse login'e zorlama (logout=true clearAuth yapıyordu)
            if (authState.isAuthenticated() && !authState.isRefreshTokenExpired()) {
              console.warn(
                '[Auth][Guard] Yenileme geçici başarısız — oturum korundu, sayfa açılıyor (sonraki API tekrar dener)'
              );
              return of(true);
            }
            navigateToLogin('koruma: token yenileme boş döndü (oturum temizlendi)');
            return of(false);
          }),
          catchError((err) => {
            if (authState.isAuthenticated() && !authState.isRefreshTokenExpired()) {
              console.warn('[Auth][Guard] Yenileme hatası (geçici) — oturum korundu', err);
              return of(true);
            }
            navigateToLogin(`koruma: token yenileme hatası ${err}`);
            return of(false);
          })
        );
      }
    }

    // In-memory state henüz yüklenmemişse ancak cookie veya storage'da oturum varsa restore et
    if (hasAuth) {
      console.info('[Auth][Guard] Bellekte oturum yok — çerez/depolamadan geri yükleniyor');
      return authState.tryRestoreSessionFromCookies().pipe(
        switchMap((success) => {
          if (success) {
            console.info('[Auth][Guard] Oturum geri yükleme başarılı');
            return of(true);
          }
          // Soft-fail: refresh hâlâ geçerliyse oturumu silme
          if (!authState.isRefreshTokenExpired() && checkAuthCookies()) {
            console.warn(
              '[Auth][Guard] Geri yükleme geçici başarısız — çerezler duruyor, giriş yok; bellek oturumu canlandırılıyor'
            );
            authState.reviveSessionFromStorage();
            return of(true);
          }
          navigateToLogin('koruma: çerez/depolamadan oturum geri yükleme başarısız');
          return of(false);
        }),
        catchError((err) => {
          if (!authState.isRefreshTokenExpired() && checkAuthCookies()) {
            console.warn('[Auth][Guard] Geri yükleme hatası (geçici) — oturum korundu', err);
            authState.reviveSessionFromStorage();
            return of(true);
          }
          navigateToLogin(`koruma: çerez/depolamadan oturum geri yükleme hatası ${err}`);
          return of(false);
        })
      );
    }

    // Hiçbir oturum izi yoksa login'e yönlendir
    navigateToLogin('koruma: oturum çerezi/depolaması yok');
    return of(false);
  }

  function navigateToLogin(detail?: string): void {
    const currentAppName = getCurrentAppName();
    const hadSession =
      authState.isAuthenticated() ||
      checkAuthCookies();

    // Sessiz login yönlendirmesini yakala (önceden console'a yazılmıyordu)
    if (hadSession) {
      authState.reportSessionEnd(
        AUTH_SESSION_END_EXPIRED,
        detail ?? `koruma → giriş sayfası (uygulama=${currentAppName ?? 'bilinmiyor'}, url=${state?.url ?? ''})`
      );
    } else {
      console.warn(
        '%c[Auth] Login sayfasına yönlendirildi (oturum yok)',
        'font-size:12px;font-weight:600;color:#64748b',
        `uygulama=${currentAppName ?? 'bilinmiyor'} url=${state?.url ?? ''}`
      );
    }

    let targetPath = '';
    if (state?.url && state.url !== '/') {
      targetPath = state.url;
    } else if (typeof window !== 'undefined' && window.location.hash) {
      const hash = window.location.hash.replace(/^#/, '');
      if (hash && hash !== '/') {
        targetPath = hash;
      }
    }

    // Default target path for clinic and lab is /main
    if (!targetPath || targetPath === '/') {
      if (currentAppName === 'lab' || currentAppName === 'clinic') {
        targetPath = '/main';
      }
    }

    if (targetPath && !targetPath.startsWith('/')) {
      targetPath = `/${targetPath}`;
    }

    let redirectParam = '';
    if (currentAppName && currentAppName !== 'account') {
      redirectParam = targetPath ? `${currentAppName}:${targetPath}` : currentAppName;
    }

    // Eğer account uygulamasındaysak, doğrudan /login'e yönlendir
    if (isAccountApp()) {
      const queryParams: Record<string, string> = {};
      if (redirectParam) {
        queryParams['redirect'] = redirectParam;
      }
      queryParams['logout'] = 'true';
      router.navigate(['/login'], { queryParams });
    } else {
      // Diğer tüm durumlarda (clinic, lab, cad ve dev varyantları), account app'e yönlendir
      const loginPath = redirectParam
        ? `/login?logout=true&redirect=${encodeURIComponent(redirectParam)}`
        : '/login?logout=true';
      authState.navigateToApp('account', loginPath, false, false);
    }
  }
}

